import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"

interface UserPreferences {
  search_history: string[]
  favorite_amenities: string[]
  preferred_price_range: [number, number]
  preferred_locations: string[]
  device_type: 'mobile' | 'tablet' | 'desktop'
  language_preference: string
  accessibility_settings: {
    high_contrast: boolean
    large_text: boolean
    reduced_motion: boolean
  }
  last_active: string
}

export const usePersonalization = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Get device type
  const getDeviceType = useCallback((): 'mobile' | 'tablet' | 'desktop' => {
    const width = window.innerWidth
    if (width < 768) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
  }, [])

  // Load user preferences
  const loadPreferences = useCallback(async () => {
    if (!user) {
      setIsLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('user_personalization')
        .select('preferences')
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) {
        throw error
      }

      if (data && data.preferences) {
        setPreferences(data.preferences as unknown as UserPreferences)
      } else {
        // Create default preferences
        const defaultPrefs: UserPreferences = {
          search_history: [],
          favorite_amenities: [],
          preferred_price_range: [0, 200],
          preferred_locations: [],
          device_type: getDeviceType(),
          language_preference: navigator.language,
          accessibility_settings: {
            high_contrast: false,
            large_text: false,
            reduced_motion: false
          },
          last_active: new Date().toISOString()
        }
        setPreferences(defaultPrefs)
        // Don't save immediately to prevent loop
      }
    } catch (error) {
      console.error('Error loading preferences:', error)
      toast({
        title: "Fehler beim Laden der Einstellungen",
        description: "Ihre persönlichen Einstellungen konnten nicht geladen werden.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }, [user, getDeviceType, toast])

  // Save preferences to database
  const savePreferences = useCallback(async (newPreferences: UserPreferences) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('user_personalization')
        .upsert({
          user_id: user.id,
          preferences: newPreferences as any,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })

      if (error) throw error
    } catch (error) {
      console.error('Error saving preferences:', error)
      // Don't show toast for database errors to avoid spam
    }
  }, [user])

  // Update specific preference
  const updatePreference = useCallback((key: keyof UserPreferences, value: any) => {
    if (!preferences) return

    const newPreferences = {
      ...preferences,
      [key]: value,
      last_active: new Date().toISOString()
    }
    
    setPreferences(newPreferences)
    savePreferences(newPreferences)
  }, [preferences, savePreferences])

  // Track user interaction
  const trackInteraction = useCallback((action: string, data?: any) => {
    if (!user || !preferences) return

    // Privacy-compliant tracking - only store essential data
    const interaction = {
      action,
      timestamp: new Date().toISOString(),
      device_type: getDeviceType(),
      // Only store anonymized/aggregated data
      ...data
    }

    console.log('User interaction tracked:', interaction)

    // Update interaction patterns for personalization
    if (action === 'search') {
      const searchTerm = data?.searchTerm?.toLowerCase()
      if (searchTerm && !preferences.search_history.includes(searchTerm)) {
        const newHistory = [...preferences.search_history.slice(-9), searchTerm]
        updatePreference('search_history', newHistory)
      }
    }

    if (action === 'amenity_filter' && data?.amenity) {
      const amenity = data.amenity
      if (!preferences.favorite_amenities.includes(amenity)) {
        const newAmenities = [...preferences.favorite_amenities.slice(-9), amenity]
        updatePreference('favorite_amenities', newAmenities)
      }
    }

    if (action === 'location_search' && data?.location) {
      const location = data.location
      if (!preferences.preferred_locations.includes(location)) {
        const newLocations = [...preferences.preferred_locations.slice(-4), location]
        updatePreference('preferred_locations', newLocations)
      }
    }
  }, [user, preferences, updatePreference, getDeviceType])

  // Get personalized recommendations
  const getRecommendations = useCallback(() => {
    if (!preferences) return []

    const recommendations = []

    // Time-based recommendations
    const hour = new Date().getHours()
    if (hour >= 18 || hour <= 6) {
      recommendations.push({
        type: 'time_based',
        title: 'Entspannte Abendunterkünfte',
        description: 'Ruhige Orte für einen entspannten Aufenthalt',
        filters: { amenities: ['Balkon', 'Garten', 'Pool'] }
      })
    } else {
      recommendations.push({
        type: 'time_based',
        title: 'Aktive Tagesunterkünfte',
        description: 'Zentrale Lage für Ihre Aktivitäten',
        filters: { amenities: ['WLAN', 'Küche', 'Parkplatz'] }
      })
    }

    // Preference-based recommendations
    if (preferences.favorite_amenities.length > 0) {
      recommendations.push({
        type: 'preference_based',
        title: 'Basierend auf Ihren Favoriten',
        description: `Unterkünfte mit ${preferences.favorite_amenities.slice(0, 2).join(' und ')}`,
        filters: { amenities: preferences.favorite_amenities }
      })
    }

    // Location-based recommendations
    if (preferences.preferred_locations.length > 0) {
      recommendations.push({
        type: 'location_based',
        title: 'Beliebte Orte für Sie',
        description: `Neue Optionen in ${preferences.preferred_locations[0]}`,
        filters: { city: preferences.preferred_locations[0] }
      })
    }

    // Device-specific recommendations
    if (preferences.device_type === 'mobile') {
      recommendations.push({
        type: 'device_optimized',
        title: 'Schnell buchbar',
        description: 'Unterkünfte mit sofortiger Bestätigung',
        filters: { quick_booking: true }
      })
    }

    return recommendations.slice(0, 3) // Limit to 3 recommendations
  }, [preferences])

  // Load preferences on mount
  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  // Update device type on resize
  useEffect(() => {
    const handleResize = () => {
      const newDeviceType = getDeviceType()
      if (preferences && preferences.device_type !== newDeviceType) {
        updatePreference('device_type', newDeviceType)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [preferences, getDeviceType, updatePreference])

  return {
    preferences,
    isLoading,
    updatePreference,
    trackInteraction,
    getRecommendations,
    savePreferences
  }
}